import type { Chat, ChatMessage, MessageInfo, MessageRecipient } from "@rhema/client";
import { Avatar, CheckCheckIcon, CheckIcon, Sheet } from "@rhema/ui";
import { useEffect, useState } from "react";
import { toneFor } from "../data/view";
import { whenLabel } from "../later/when";
import { useClient } from "../session/session";
import { textOf } from "./MessageList";

/** "today at 10:42" or "on Monday at 10:42", to follow a word such as Read. */
function when(iso: string): string {
  const label = whenLabel(iso);
  return /^(Today|Tomorrow|Yesterday) /.test(label)
    ? label.charAt(0).toLowerCase() + label.slice(1)
    : `on ${label}`;
}

/** "Read today at 10:42", or just "Read" when the server did not keep the time. */
function stateLine(r: MessageRecipient): string {
  if (r.read) return r.read_at ? `Read ${when(r.read_at)}` : "Read";
  if (r.delivered) return r.delivered_at ? `Delivered ${when(r.delivered_at)}` : "Delivered";
  return "Not delivered yet";
}

/**
 * Message info for one of your messages: when it reached each person and when they read it. Reads
 * show only where both of you have read receipts on.
 */
export function MessageInfoSheet({
  chat,
  message,
  onClose,
}: {
  chat: Chat;
  message: ChatMessage | null;
  onClose: () => void;
}) {
  const client = useClient();
  const [info, setInfo] = useState<MessageInfo | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const id = message?.id;

  useEffect(() => {
    setInfo(null);
    setProblem(null);
    if (!id) return;
    let live = true;
    client
      .messageInfo(chat.id, id)
      .then((i) => live && setInfo(i))
      .catch((e: Error) => live && setProblem(e.message || "Message info could not be loaded."));
    return () => {
      live = false;
    };
  }, [client, chat.id, id]);

  const nameOf = (userId: string) =>
    chat.members.find((m) => m.user.id === userId)?.user ?? {
      id: userId,
      display_name: "Someone",
      avatar_url: null,
    };

  return (
    <Sheet
      open={message !== null}
      onClose={onClose}
      title="Message info"
      subtitle={info ? `Sent ${when(info.sent_at)}` : undefined}
      className="pb-7"
    >
      {message && (
        <blockquote className="m-0 mt-3 max-h-28 overflow-y-auto rounded-field bg-blue-container px-3.5 py-2.5 text-[14.5px] whitespace-pre-wrap [overflow-wrap:anywhere]">
          {textOf(message)}
        </blockquote>
      )}
      {problem && (
        <p role="alert" className="m-0 mt-3 text-sm font-semibold text-scarlet">
          {problem}
        </p>
      )}
      {!info && !problem && <p className="m-0 mt-3 text-sm text-ink-meta">Loading message info.</p>}
      {info && (
        <ul aria-label="Recipients" className="m-0 mt-3 flex list-none flex-col p-0">
          {info.recipients.map((r) => {
            const user = nameOf(r.user_id);
            return (
              <li
                key={r.user_id}
                className="flex items-center gap-3 border-t border-line py-2.5 first:border-t-0"
              >
                <Avatar name={user.display_name} src={user.avatar_url} tone={toneFor(user.id)} size={36} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[15px] font-semibold">{user.display_name}</span>
                  <span className="text-[13px] text-ink-secondary">{stateLine(r)}</span>
                  {r.read && r.delivered && (
                    <span className="text-[12.5px] text-ink-meta">
                      {r.delivered_at ? `Delivered ${when(r.delivered_at)}` : "Delivered"}
                    </span>
                  )}
                </span>
                {r.read ? (
                  <CheckCheckIcon size={18} className="flex-none text-blue" label="Read" />
                ) : r.delivered ? (
                  <CheckCheckIcon size={18} className="flex-none text-ink-meta" label="Delivered" />
                ) : (
                  <CheckIcon size={18} className="flex-none text-ink-meta" label="Sent" />
                )}
              </li>
            );
          })}
          {info.recipients.length === 0 && (
            <li className="py-2 text-sm text-ink-secondary">There is nobody else in this chat yet.</li>
          )}
        </ul>
      )}
    </Sheet>
  );
}
